import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button, Container, Stack, Text, Title } from '@mantine/core'

interface Props {
  readonly children: ReactNode
  /** Stage 1 wires the Sentry-compatible reporter here (06 §6). */
  readonly onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  readonly error: Error | null
}

/**
 * Top-level render-crash boundary — docs/spec/01 §7.
 *
 * A render crash gets a recoverable fallback, never a white screen. Note what
 * does NOT belong here: data and mutation errors are not thrown to boundaries
 * (01 §7). They surface through the 06 layer as inline states and
 * notifications, because a failed submit should not blank the page a
 * transcriber was working in.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Render crashes are genuinely unexpected, so they ARE reported (06 §6) —
    // unlike expected 4xx, which are user-facing and not incidents.
    this.props.onError?.(error, info)
  }

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children

    return (
      <Container size="sm" py="xl">
        <Stack gap="sm" align="flex-start">
          <Title order={1} size="h3">
            Something went wrong on our end
          </Title>
          {/* 06 R2: reassure that work is not lost, and never imply data loss. */}
          <Text c="dimmed">Your work was not lost. Try again, or reload the page.</Text>
          <Button variant="light" mt="sm" onClick={() => this.setState({ error: null })}>
            Try again
          </Button>
        </Stack>
      </Container>
    )
  }
}
